import { MapBounds } from '../../graphql/graphql';

export const getMapOptions = (
  isSmall: boolean,
  isSmallOrLargeDesktop: boolean
): google.maps.MapOptions => ({
  gestureHandling: isSmall ? 'greedy' : 'auto',
  disableDefaultUI: true,
  clickableIcons: false,
  fullscreenControl: false,
  fullscreenControlOptions: {
    position: google.maps.ControlPosition.BLOCK_START_INLINE_END
  },
  zoomControl: false,
  mapTypeControl: true,
  mapTypeControlOptions: {
    style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
    position:
      google.maps.ControlPosition[
        isSmallOrLargeDesktop
          ? 'BLOCK_START_INLINE_END'
          : 'BLOCK_START_INLINE_START'
      ]
  },
  streetViewControl: true,
  streetViewControlOptions: {
    position: google.maps.ControlPosition.RIGHT_BOTTOM
  },
  cameraControl: true,
  cameraControlOptions: {
    position: google.maps.ControlPosition.INLINE_END_BLOCK_END
  },
  maxZoom: 21
});

export const getMapPadding = ({
  isSmall,
  mapHeight
}: {
  isSmall: boolean;
  mapHeight: number;
}) => {
  if (isSmall) {
    return Math.round(mapHeight * 0.08);
  }

  return Math.round(mapHeight * 0.12);
};

export const mapBoundsToGoogleBounds = (mapBounds: MapBounds) => ({
  north: mapBounds.topLeft.latitude,
  south: mapBounds.bottomRight.latitude,
  west: mapBounds.topLeft.longitude,
  east: mapBounds.bottomRight.longitude
});

export const getShrunkBoundsFromMap = (map: google.maps.Map) => {
  const bounds = map.getBounds();
  if (!bounds) return null;

  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const shrinkRatio = 0.05;

  const latDiff = ne.lat() - sw.lat();
  const lngDiff = ne.lng() - sw.lng();

  return {
    topLeft: {
      latitude: ne.lat() - latDiff * shrinkRatio,
      longitude: sw.lng() + lngDiff * shrinkRatio
    },
    bottomRight: {
      latitude: sw.lat() + latDiff * shrinkRatio,
      longitude: ne.lng() - lngDiff * shrinkRatio
    }
  };
};

export const panToMarkerWithDesktopOffset = (
  map: google.maps.Map,
  position: { lat: number; lng: number },
  options?: {
    isSmallOrLargeDesktop?: boolean;
    offsetRatio?: number;
    offsetY?: number;
  }
) => {
  const {
    isSmallOrLargeDesktop = false,
    offsetRatio = 0.2,
    offsetY = 0
  } = options ?? {};

  if (!isSmallOrLargeDesktop) {
    map.panTo(position);
    return;
  }

  const projection = map.getProjection();
  if (!projection) {
    map.panTo(position);
    return;
  }

  const zoom = map.getZoom() ?? 0;
  const scale = 2 ** zoom;

  const mapWidth = map.getDiv().offsetWidth;
  const offsetX = Math.round(mapWidth * offsetRatio);

  const worldPoint = projection.fromLatLngToPoint(
    new google.maps.LatLng(position.lat, position.lng)
  );

  if (!worldPoint) {
    map.panTo(position);
    return;
  }

  const newPoint = new google.maps.Point(
    worldPoint?.x - offsetX / scale,
    worldPoint?.y + offsetY / scale
  );

  const newLatLng = projection.fromPointToLatLng(newPoint);

  if (!newLatLng) {
    map.panTo(position);
    return;
  }

  map.panTo(newLatLng);
};

export const isPointInBackendBounds = (
  lat: number,
  lng: number,
  bounds: MapBounds
) => {
  const { topLeft, bottomRight } = bounds;

  return (
    lat <= topLeft.latitude &&
    lat >= bottomRight.latitude &&
    lng >= topLeft.longitude &&
    lng <= bottomRight.longitude
  );
};

/**
 * Checks if the map movement is significant enough to trigger a new API request.
 * Calculated in screen pixels based on the current zoom level.
 */
export const isMovementSignificant = (
  lastPoint: google.maps.Point | null,
  currentPoint: google.maps.Point | null,
  zoom: number,
  threshold: number
): boolean => {
  if (!lastPoint || !currentPoint) return true;

  // At each zoom level, the number of pixels doubles: 2^zoom
  // eslint-disable-next-line no-restricted-properties
  const scale = Math.pow(2, zoom);

  const dx = Math.abs(lastPoint.x - currentPoint.x) * scale;
  const dy = Math.abs(lastPoint.y - currentPoint.y) * scale;

  return dx > threshold || dy > threshold;
};
